import "@bleu.builders/ui/index.css";

import { i18n, RailsAppProvider, ThemeProvider } from "@bleu.builders/ui";
import { PostHogProvider } from "posthog-js/react";
import React from "react";
import { I18nextProvider } from "react-i18next";
import {
  createBrowserRouter,
  Navigate,
  RouteObject,
  RouterProvider,
} from "react-router-dom";

import { AdminPreviewProvider } from "#/contexts/AdminPreviewContext";
import { AuthenticationProvider } from "#/contexts/AuthenticationContext";
import { Feature, FeaturesProvider } from "#/contexts/FeaturesContext";
import { ImpersonationProvider } from "#/contexts/ImpersonationContext";
import { OrganizationProvider } from "#/contexts/OrganizationContext";
import { RailsFlashProvider } from "#/contexts/RailsFlashContext";
import { UserProvider } from "#/contexts/UserContext";
import {
  AdminPagePath,
  buildEditPage,
  buildIndexPage,
  buildIndexPageActionButton,
  buildNewPage,
  buildShowPage,
  Dashboard,
  generateAction,
  generateLoader,
  UserPagePath,
} from "#/lib/pagesHelper";
import {
  canCreateDiagnosis,
  canViewNewsArticle,
  canViewPetitionModel,
  userIsAdmin,
} from "#/lib/permissions";
import DiagnosisMatches from "#/pages/diagnoses/matches";
import RecentNewsArticles from "#/pages/news_articles/(components)/news_article_table/index";
import NewsArticlesLatestTable from "#/pages/news_articles/(components)/news_article_table/latest";

import { WizardForm } from "./(components)/wizard/Form";
import DashboardPage from "./dashboard";
import DiagnosesPage from "./diagnoses";
import { ExternalDiagnosisForm } from "./diagnoses/external_diagnosis_form";
import { ExternalDiagnosisSuccess } from "./diagnoses/external_diagnosis_success";
import ErrorBoundary from "./ErrorBoundary";
import EventDetails from "./events/details";
import { NewEvent } from "./events/new";
import Layout from "./Layout";
import LoginPage from "./login";
import { StepOneEmailInputForm } from "./login/(components)/StepOneEmailInputForm";
import { StepTwoOTPInputForm } from "./login/(components)/StepTwoOTPInputForm";
import SingupPage from "./login/signup";
import NewsArticleDetails from "./news_articles/details";
import { OrganizationDetails } from "./organization/details";
import OrganizationSettingsPage from "./settings/organization/page";
import { Calculator } from "./simulate-calculation";
import Subscription from "./subscription/page";
import ThesesPage from "./theses";
import { ThesisTable } from "./theses/(components)/ThesesTable";
import Onboarding from "./user/onboarding";
import Transactions from "./user/transactions";

const POSTHOG_API_KEY = "phc_e6ZzODN2pEAFUPP3L94cZCiu6TuNhmlB3cZ9LzcsGLO";

const options = {
  api_host: "https://app.posthog.com",
};

const ADMIN_PAGES = [
  {
    path: "/admin",
    element: <Layout showSidebar adminOnly />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to="./theses" />,
      },
      {
        path: "theses",
        children: [
          {
            index: true,
            ...buildIndexPage({
              modelName: "Teses",
              fetchPath: AdminPagePath.THESES,
              action: buildIndexPageActionButton({
                link: AdminPagePath.THESES_NEW,
                text: `Criar tese`,
              }),
              searchKey: "title",
              predicate: userIsAdmin,
            }),
          },
          {
            path: "new",
            ...buildNewPage({
              modelName: "Tese",
              actionPath: AdminPagePath.THESES,
              loaderPath: AdminPagePath.THESES_NEW,
              Component: () => <WizardForm />,
              predicate: userIsAdmin,
            }),
          },
          {
            path: ":thesis_id",
            children: [
              {
                index: true,
                element: <Navigate to="./edit" />,
              },
              {
                path: "edit",
                ...buildEditPage({
                  modelName: "Tese",
                  actionPath: AdminPagePath.THESES_UPDATE,
                  loaderPath: AdminPagePath.THESES_EDIT,
                  Component: () => <WizardForm mode="update" />,
                  predicate: userIsAdmin,
                }),
              },
              {
                path: "thesis_updates",
                children: [
                  {
                    index: true,
                    ...buildIndexPage({
                      modelName: "Atualizações",
                      fetchPath: AdminPagePath.THESIS_UPDATES,
                      action: buildIndexPageActionButton({
                        link: AdminPagePath.THESIS_UPDATES_NEW,
                        text: "Criar Nova Atualização",
                        setParams: true,
                      }),
                      searchKey: "summary",
                      predicate: userIsAdmin,
                    }),
                  },
                  {
                    path: "new",
                    ...buildNewPage({
                      modelName: "Atualização",
                      actionPath: AdminPagePath.THESIS_UPDATES,
                      loaderPath: AdminPagePath.THESIS_UPDATES_NEW,
                      predicate: userIsAdmin,
                    }),
                  },
                  {
                    path: ":thesis_update_id",
                    children: [
                      {
                        index: true,
                        element: <Navigate to="./edit" />,
                      },
                      {
                        path: "edit",
                        ...buildEditPage({
                          modelName: "Testing",
                          actionPath: AdminPagePath.THESIS_UPDATES_UPDATE,
                          loaderPath: AdminPagePath.THESIS_UPDATES_EDIT,
                          predicate: userIsAdmin,
                        }),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "petition_models",
        children: [
          {
            index: true,
            ...buildIndexPage({
              modelName: "Modelos de Petição",
              fetchPath: AdminPagePath.PETITION_MODELS,
              action: buildIndexPageActionButton({
                link: AdminPagePath.PETITION_MODELS_NEW,
                text: `Criar Modelo de Petição`,
              }),
              searchKey: "title",
              predicate: userIsAdmin,
            }),
          },
          {
            path: "new",
            ...buildNewPage({
              modelName: "Modelo de Petição",
              actionPath: AdminPagePath.PETITION_MODELS,
              loaderPath: AdminPagePath.PETITION_MODELS_NEW,
              predicate: userIsAdmin,
            }),
          },
          {
            path: ":petition_model_id",
            children: [
              {
                index: true,
                element: <Navigate to="./edit" />,
              },
              {
                path: "edit",
                ...buildEditPage({
                  modelName: "Modelo de Petição",
                  actionPath: AdminPagePath.PETITION_MODELS_UPDATE,
                  loaderPath: AdminPagePath.PETITION_MODELS_EDIT,
                  predicate: userIsAdmin,
                }),
              },
            ],
          },
        ],
      },
      {
        path: "news_articles",
        children: [
          {
            index: true,
            ...buildIndexPage({
              modelName: "Monitoramento Tributário",
              fetchPath: AdminPagePath.NEWS_ARTICLES,
              action: buildIndexPageActionButton({
                link: AdminPagePath.NEWS_ARTICLES_NEW,
                text: `Criar Notícia`,
              }),
              searchKey: "title",
              predicate: userIsAdmin,
            }),
          },
          {
            path: "new",
            ...buildNewPage({
              modelName: "Notícia (Monitoramento Tributário)",
              actionPath: AdminPagePath.NEWS_ARTICLES,
              loaderPath: AdminPagePath.NEWS_ARTICLES_NEW,
              predicate: userIsAdmin,
            }),
          },
          {
            path: ":news_article_id",
            children: [
              {
                index: true,
                element: <Navigate to="./edit" />,
              },
              {
                path: "edit",
                ...buildEditPage({
                  modelName: "Notícia (Monitoramento Tributário)",
                  actionPath: AdminPagePath.NEWS_ARTICLES_UPDATE,
                  loaderPath: AdminPagePath.NEWS_ARTICLES_EDIT,
                  predicate: userIsAdmin,
                }),
              },
            ],
          },
        ],
      },
      {
        path: "billing_plans",
        children: [
          {
            index: true,
            ...buildIndexPage({
              modelName: "Planos",
              fetchPath: AdminPagePath.BILLING_PLANS,
              action: buildIndexPageActionButton({
                link: AdminPagePath.BILLING_PLANS_NEW,
                text: `Criar Plano`,
              }),
              searchKey: "name",
              predicate: userIsAdmin,
            }),
          },
          {
            path: "new",
            ...buildNewPage({
              modelName: "Plano",
              actionPath: AdminPagePath.BILLING_PLANS,
              loaderPath: AdminPagePath.BILLING_PLANS_NEW,
              predicate: userIsAdmin,
            }),
          },
        ],
      },
      {
        path: "billing_coupons",
        children: [
          {
            index: true,
            ...buildIndexPage({
              modelName: "Cupons",
              fetchPath: AdminPagePath.BILLING_COUPONS,
              action: buildIndexPageActionButton({
                link: AdminPagePath.BILLING_COUPONS_NEW,
                text: `Criar Cupom`,
              }),
              searchKey: "code",
              predicate: userIsAdmin,
            }),
          },
          {
            path: "new",
            ...buildNewPage({
              modelName: "Cupom",
              actionPath: "/admin/billing_coupons/",
              loaderPath: "/admin/billing_coupons/new",
              predicate: userIsAdmin,
            }),
          },
        ],
      },
      {
        path: "users",
        children: [
          {
            index: true,
            ...buildIndexPage({
              modelName: "Usuários",
              fetchPath: AdminPagePath.USERS,
              action: buildIndexPageActionButton({
                link: AdminPagePath.USERS_NEW,
                text: `Criar Usuário`,
              }),
              searchKey: "email",
              predicate: userIsAdmin,
            }),
          },
          {
            path: "new",
            ...buildNewPage({
              modelName: "Usuário",
              actionPath: "/admin/users/",
              loaderPath: "/admin/users/new",
              predicate: userIsAdmin,
            }),
          },
        ],
      },
      {
        path: "organizations",
        children: [
          {
            index: true,
            ...buildIndexPage({
              modelName: "Organizações",
              fetchPath: AdminPagePath.ORGANIZATIONS,
              action: undefined,
              searchKey: "email",
              predicate: userIsAdmin,
            }),
          },
          {
            path: ":organization_id",
            element: <OrganizationDetails />,
            loader: generateLoader(AdminPagePath.ORGANIZATION_DETAILS),
            children: [
              {
                index: true,
                element: (
                  <Dashboard
                    fetchPath={AdminPagePath.ORGANIZATION_INVOICES}
                    action={undefined}
                    searchKey="email"
                  />
                ),
              },
              {
                path: "users",
                element: (
                  <Dashboard
                    fetchPath={AdminPagePath.ORGANIZATION_USERS}
                    action={undefined}
                    searchKey="email"
                  />
                ),
              },
              {
                path: "transactions",
                element: (
                  <Dashboard
                    fetchPath={AdminPagePath.ORGANIZATION_TRANSACTIONS}
                    action={undefined}
                    searchKey="reason"
                  />
                ),
              },
            ],
          },
        ],
      },
      {
        path: "billing_plans",
        ...buildIndexPage({
          modelName: "Planos",
          fetchPath: AdminPagePath.BILLING_PLANS,
          action: undefined,
          searchKey: "code",
          predicate: userIsAdmin,
        }),
      },
      {
        path: "checkout_links",
        ...buildIndexPage({
          modelName: "Links para Checkout",
          fetchPath: AdminPagePath.CHECKOUT_LINKS,
          action: undefined,
          searchKey: "organization",
          predicate: userIsAdmin,
        }),
      },
      {
        path: "events",
        children: [
          {
            index: true,
            ...buildIndexPage({
              modelName: "Eventos de Notificação",
              fetchPath: AdminPagePath.EVENTS,
              action: buildIndexPageActionButton({
                link: AdminPagePath.EVENTS_NEW,
                text: `Criar Evento`,
              }),
              searchKey: "organization",
              predicate: userIsAdmin,
            }),
          },
          {
            path: "new",
            element: <NewEvent actionPath={AdminPagePath.EVENTS} />,
            action: generateAction(AdminPagePath.EVENTS, "Evento", "create"),
          },
          {
            path: ":event_id",
            children: [
              {
                index: true,
                element: <Navigate to="./edit" />,
              },
              {
                path: "edit",
                loader: generateLoader(AdminPagePath.EVENTS_EDIT),
                action: generateAction(
                  AdminPagePath.EVENTS_UPDATE,
                  "Evento",
                  "update",
                ),
                element: <NewEvent actionPath={AdminPagePath.EVENTS_UPDATE} />,
              },
            ],
          },
        ],
      },
    ],
  },
] as const as RouteObject[];

const LOGIN_PAGES = [
  {
    path: "/login",
    element: <LoginPage />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <StepOneEmailInputForm />,
        action: StepOneEmailInputForm.action,
      },
      {
        path: "verify",
        element: <StepTwoOTPInputForm />,
        action: StepTwoOTPInputForm.action,
      },
    ],
  },
  {
    path: "/signup",
    element: <SingupPage />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <StepOneEmailInputForm />,
        action: StepOneEmailInputForm.action,
      },
      {
        path: "verify",
        element: <StepTwoOTPInputForm />,
        action: StepTwoOTPInputForm.action,
      },
    ],
  },
] as const as RouteObject[];

const EXTERNAL_DIAGNOSIS_PAGES = [
  {
    path: "/c/:diagnosis_id",
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        ...buildEditPage({
          modelName: "Diagnóstico",
          actionPath: UserPagePath.DIAGNOSES_UPDATE,
          loaderPath: UserPagePath.DIAGNOSES_EDIT,
          redirectAfterAction: (data: { data: { reference_code: string } }) => {
            return "/c/:diagnosis_id/success".replace(
              ":diagnosis_id",
              data.data.reference_code,
            );
          },
        }),
        element: (
          <div className="px-4">
            <ExternalDiagnosisForm />
          </div>
        ),
      },
      {
        path: "success",
        element: <ExternalDiagnosisSuccess />,
      },
    ],
  },
  {
    path: "/s/:reusable_diagnosis_link_code",
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        ...buildNewPage({
          modelName: "Diagnóstico",
          loaderPath: UserPagePath.DIAGNOSES_NEW_EXTERNAL,
          actionPath: UserPagePath.DIAGNOSES_EXTERNAL,
          predicate: () => true,
          Component: () => (
            <div>
              <ExternalDiagnosisForm mode="create" />
            </div>
          ),
          redirectAfterAction: (data: { data: { reference_code: string } }) => {
            return "/s/:diagnosis_id/success".replace(
              ":diagnosis_id",
              data.data.reference_code,
            );
          },
        }),
      },
      {
        path: "success",
        element: <ExternalDiagnosisSuccess />,
      },
      {
        path: "expired",
        element: <div>Link expirado</div>,
      },
    ],
  },
] as const as RouteObject[];

const FALLBACK_PAGE = [
  {
    path: "*",
    element: <Navigate to="/login" replace />,
    errorElement: <ErrorBoundary />,
  },
] as const as RouteObject[];

const END_USER_NEWS_ARTICLES_PAGES = [
  {
    path: "news_articles",
    children: [
      {
        index: true,
        element: <RecentNewsArticles />,
      },
      {
        path: ":news_article_id",
        ...buildShowPage({
          modelName: "Notícia",
          loaderPath: UserPagePath.NEWS_ARTICLE,
          predicate: canViewNewsArticle,
          Component: () => <NewsArticleDetails />,
        }),
      },
      {
        path: "latest",
        element: <NewsArticlesLatestTable />,
      },
    ],
  },
] as const as RouteObject[];

const END_USER_DASHBOARD_PAGES = [
  {
    path: "/",
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
    ],
  },
] as const as RouteObject[];

const END_USER_DIAGNOSES_PAGES = [
  {
    path: "diagnoses",
    children: [
      {
        index: true,
        element: <DiagnosesPage />,
      },
      {
        path: "new",
        ...buildNewPage({
          modelName: "Diagnóstico",
          actionPath: UserPagePath.DIAGNOSES,
          redirectAfterAction: (data: { id: string }) => {
            return UserPagePath.DIAGNOSES_MATCHES.replace(
              ":diagnosis_id",
              data.id,
            );
          },
          loaderPath: UserPagePath.DIAGNOSES_NEW,
          predicate: canCreateDiagnosis,
          Component: () => (
            <div>
              <WizardForm />
            </div>
          ),
        }),
      },
      {
        path: ":diagnosis_id",
        children: [
          {
            index: true,
            element: <Navigate to="./edit" />,
          },
          {
            path: "edit",
            ...buildEditPage({
              modelName: "Diagnóstico",
              actionPath: UserPagePath.DIAGNOSES_UPDATE,
              loaderPath: UserPagePath.DIAGNOSES_EDIT,
              predicate: canCreateDiagnosis,
              Component: () => (
                <div>
                  <WizardForm mode="update" />
                </div>
              ),
            }),
          },
          {
            path: "matches",
            element: <DiagnosisMatches />,
            loader: generateLoader(UserPagePath.DIAGNOSES_UPDATE),
          },
        ],
      },
    ],
  },
] as const as RouteObject[];

const END_USER_THESES_PAGES = [
  {
    path: "theses",
    element: <ThesesPage />,
    children: [
      // key attribute is necessary here to force a re-render when the user changes the page
      {
        index: true,
        element: (
          <ThesisTable
            key="default"
            fetchPath={UserPagePath.THESES}
            sort="tax"
          />
        ),
      },
      {
        path: ":id",
        element: <ThesisTable key=":id" fetchPath={UserPagePath.THESES} />,
      },
      {
        path: "business_activity",
        element: (
          <ThesisTable
            fetchPath={UserPagePath.THESES}
            key="business_activity"
            group="business_activity"
            sort="business_activity"
            showGroupOptions={false}
          />
        ),
      },
      {
        path: "trending",
        element: (
          <ThesisTable
            fetchPath={UserPagePath.TRENDING_THESES}
            key="trending"
            sort="trending"
            group="trending"
            showGroupOptions={false}
          />
        ),
      },
      {
        path: "latest",
        element: (
          <ThesisTable
            fetchPath={UserPagePath.LATEST_THESIS}
            key="latest"
            sort="tax"
          />
        ),
      },
      {
        path: "purchased",
        element: (
          <ThesisTable
            fetchPath={UserPagePath.PURCHASED_THESES}
            key="purchased"
            sort="tax"
          />
        ),
      },
    ],
  },
] as const as RouteObject[];

const END_USER_SETTINGS_PAGES = [
  {
    path: "me",
    ...buildEditPage({
      modelName: "Perfil",
      actionPath: UserPagePath.SETTINGS_USER_UPDATE,
      loaderPath: UserPagePath.SETTINGS_USER_EDIT,
    }),
  },
  {
    path: "billing",
    element: <Subscription />,
  },
  {
    path: "transactions",
    element: <Transactions />,
  },
  {
    path: "brand_theme",
    ...buildEditPage({
      modelName: "Design",
      actionPath: UserPagePath.ORGANIZATION_DESIGN_SETTINGS_UPDATE,
      loaderPath: UserPagePath.ORGANIZATION_DESIGN_SETTINGS_EDIT,
    }),
  },
  {
    path: "organization",
    ...buildEditPage({
      modelName: "Organização",
      actionPath: UserPagePath.ORGANIZATION_UPDATE,
      loaderPath: UserPagePath.ORGANIZATION_EDIT,
      redirectAfterAction: (data) => {
        return UserPagePath.ORGANIZATION_SETTINGS;
      },
    }),
    element: <OrganizationSettingsPage />,
  },
] as const as RouteObject[];

const END_USER_ONBOARDING_PAGES = [
  {
    path: "users",
    children: [
      {
        path: ":user_id",
        children: [
          {
            path: "onboarding",
            element: <Onboarding />,
            loader: generateLoader(UserPagePath.USER_ONBOARDING_NEW),
            action: generateAction(
              UserPagePath.USER_ONBOARDING,
              "User",
              "create",
              false,
            ),
          },
        ],
      },
    ],
  },
] as const as RouteObject[];

const END_USER_EVENTS_PAGES = [
  {
    path: "events",
    children: [
      {
        path: ":event_id",
        element: <EventDetails />,
        loader: generateLoader(UserPagePath.EVENT_DETAILS),
      },
    ],
  },
] as const as RouteObject[];

const END_USER_PAGES = [
  {
    path: "/",
    element: <Layout />,
    errorElement: <ErrorBoundary />,
    children: [
      ...END_USER_NEWS_ARTICLES_PAGES,
      ...END_USER_DIAGNOSES_PAGES,
      ...END_USER_THESES_PAGES,
      ...END_USER_ONBOARDING_PAGES,
      ...END_USER_DASHBOARD_PAGES,
      ...END_USER_EVENTS_PAGES,
      {
        path: "simulate-calculation/:calculator_id",
        element: <Calculator />,
      },
    ],
  },
  {
    path: "/settings",
    element: <Layout showSidebar />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to="billing" replace />,
      },
      ...END_USER_SETTINGS_PAGES,
    ],
  },
] as const as RouteObject[];

const UNRESTRICTED_PAGES = [
  ...LOGIN_PAGES,
  ...EXTERNAL_DIAGNOSIS_PAGES,
  ...ADMIN_PAGES,
  ...END_USER_PAGES,
  ...FALLBACK_PAGE,
] as const as RouteObject[];

const END_USER_PETITION_MODELS_PAGES = [
  {
    path: "petition_models",
    children: [
      {
        index: true,
        ...buildIndexPage({
          modelName: "Petições",
          fetchPath: UserPagePath.PETITIONS,
          action: undefined,
          searchKey: "title",
          predicate: canViewPetitionModel,
        }),
      },
      {
        path: ":petition_model_id",
        ...buildShowPage({
          modelName: "Petição",
          loaderPath: UserPagePath.PETITION,
          predicate: canViewPetitionModel,
        }),
      },
    ],
  },
] as const as RouteObject[];

const FEATURE_PAGES = {
  [Feature.SHOW_PETITION_MODELS]: END_USER_PETITION_MODELS_PAGES,
};

const App = ({ id = "app", page = null }) => {
  const isServer = typeof window === "undefined";
  const el = isServer ? null : document.getElementById(id);
  const initialPage = page || JSON.parse(el.dataset.page);

  const { user, flash_messages, true_user, current_user, features } =
    initialPage;

  const FEATURE_GATED_PAGES = Object.entries(FEATURE_PAGES)
    .filter(([feature, pages]) => features[feature])
    .reduce((acc, [_, pages]) => {
      acc.push(...pages);
      return acc;
    }, []);

  const router = createBrowserRouter(
    UNRESTRICTED_PAGES.map((page) => {
      if (!page.children) return page;
      if (page.path !== "/") return page;

      return {
        ...page,
        children: [...FEATURE_GATED_PAGES, ...page.children],
      } as RouteObject;
    }),
  );

  return (
    <React.StrictMode>
      <I18nextProvider i18n={i18n.default}>
        <RailsAppProvider>
          <ThemeProvider defaultTheme="light" storageKey="clickfiscal-ui-theme">
            <RailsFlashProvider messages={flash_messages}>
              <AuthenticationProvider>
                <OrganizationProvider organization={user?.organization}>
                  <ImpersonationProvider
                    trueUser={true_user}
                    currentUser={current_user}
                  >
                    <AdminPreviewProvider preview={initialPage.preview}>
                      <UserProvider user={user}>
                        <FeaturesProvider features={features}>
                          <PostHogProvider
                            apiKey={POSTHOG_API_KEY}
                            options={options}
                          >
                            <RouterProvider
                              router={router}
                              future={{ v7_startTransition: true }}
                            />
                          </PostHogProvider>
                        </FeaturesProvider>
                      </UserProvider>
                    </AdminPreviewProvider>
                  </ImpersonationProvider>
                </OrganizationProvider>
              </AuthenticationProvider>
            </RailsFlashProvider>
          </ThemeProvider>
        </RailsAppProvider>
      </I18nextProvider>
    </React.StrictMode>
  );
};

export default App;
